import { AnalyticsEventClaims } from "./event-claims.js";

// Used to hand batches of analytics events to the transport once the visit they belong to has been recorded, and
// to carry across a page leave any batch the browser never sent.
//
// A batch waits on `ahoy.ready` while the visit request is in flight. That keeps an event from reaching the
// endpoint before the visit carrying its referrer and campaign parameters, which `Ahoy::Store#track_visit` reads
// to attribute the visit. A page left before the visit request completes never becomes ready, so its waiting
// batches are left in the outbox. A batch the browser refuses is held and offered again after `retryDelay`, once the
// beacons in flight have had time to complete. A leave leaves a refused batch in the outbox too. The next page sends
// what the outbox holds once its own `ahoy.ready` fires. While the `ahoy_track` cookie is set, `ahoy.js` repeats a
// visit request that never completed, so by then the visit has been recorded.
//
// A batch the queue stored as its events were appended keeps its outbox key, and stays stored until it leaves by the
// transport or `ahoy.track`, which removes the stored copy. A leave lets go of what this page holds, which leaves it
// for the next page, and stores any batch that is not stored yet. A page that dies without a leave event leaves its
// stored batches the same way. The transport and `ahoy.track` routes claim the events first (`AnalyticsEventClaims`),
// so an event that another page holds a copy of, in a duplicated tab or a same-origin frame, goes out once.
//
// Batching trips the fallback when it shows a fault: the transport reports a request the endpoint rejected, the
// outbox holds more than `maxStoredBatches` unexpired batches when a page starts or has no room for a batch, or this
// code throws. From then on every event goes to `ahoy.track`. What is pending here goes to the outbox, and what the
// outbox holds goes to `ahoy.track` one batch every `releaseInterval`, because `ahoy.js` sends each event as a beacon
// of its own and drops one the browser refuses. A batch refused `maxAttempts` times takes the same paced route.
// `ahoy.track` records an event under the current visit, so an event released from the outbox loses the visit it was
// stored with. The event keeps the time it happened while that is within `Ahoy.visit_duration`, raised to the start of
// the current visit when it precedes it. An older event takes the time it is saved.
//
// Decision record: https://github.com/combinaut/sparkle/wiki/ADR-0029-Batch-Analytics-Events-in-Our-Own-Modules-Around-a-Vendored-Ahoy-JS
export class AnalyticsEventDelivery {
  // @param ahoy {Object} the ahoy.js instance that owns the visit
  // @param transport {AnalyticsEventTransport}
  // @param outbox {AnalyticsEventOutbox}
  // @param fallback {AnalyticsEventFallback}
  // @param options {Object}
  //   - `retryDelay`: the milliseconds before a refused batch is offered again
  //   - `maxAttempts`: the refusals after which a batch goes to `ahoy.track` rather than being retried
  //   - `maxStoredBatches`: the batches the outbox may hold at the start of a page before it counts as not draining
  //   - `releaseInterval`: the milliseconds between batches released from the outbox once the fallback is active
  //   - `batching`: false when the page records events through `ahoy.track` alone, so `resume` releases what the
  //     outbox holds to `ahoy.track` rather than sending it. Defaults to true.
  //   - `track`: called with the events this hands to `ahoy.track`, and the tokens they belong to when those are
  //     known, in place of calling `ahoy.track` for each
  //   - `onHandOff`: called with events as they leave this page, whether the browser took them, the outbox stored
  //     them, or they went to `track`
  //   - `claims`: the `AnalyticsEventClaims` the events are claimed through. Defaults to the outbox's, from
  //     `AnalyticsEventClaims.forOutbox`.
  constructor(ahoy, transport, outbox, fallback, options) {
    options = options || {};

    this.ahoy = ahoy;
    this.transport = transport;
    this.outbox = outbox;
    this.fallback = fallback;
    this.retryDelay = options.retryDelay || 1000;
    this.maxAttempts = options.maxAttempts || 10;
    this.maxStoredBatches = options.maxStoredBatches || 10;
    this.releaseInterval = options.releaseInterval || 500;
    this.batching = options.batching !== false;
    this.trackEvents =
      options.track || ((events) => events.forEach((event) => ahoy.track(event.name, event.properties, event.time)));
    this.onHandOff = options.onHandOff || function () {};
    this.claims = options.claims || AnalyticsEventClaims.forOutbox(outbox);
    this.waiting = new Set();
    this.refused = [];
    this.retryTimer = null;
    this.releasing = false;
    this.leaving = false;

    this.fallback.onTrip(() => this.release());
  }

  // Sends the events under `tokens` once the visit is ready, or under the current tokens when `tokens` carries no visit
  // token. While the fallback is active, the events go to `ahoy.track` through the outbox (`trackLater`). While the
  // page is being left, events the visit is not yet ready for are left in the outbox.
  // @param key {String} optional, the outbox key this page holds the events under
  send(events, tokens, key) {
    if (this.fallback.active()) {
      this.trackLater(events, tokens, key);
      return;
    }

    const batch = { events: events, key: key };

    if (tokens && tokens.visit_token) {
      batch.tokens = tokens;
    }

    this.waiting.add(batch);
    this.guard(() => {
      this.ahoy.ready(() => this.dispatchWaiting(batch));

      if (this.leaving) {
        this.stash();
      }
    });
  }

  // Dispatches the batch if it is still waiting on the visit. A batch that `stash` or `release` has already taken from
  // `waiting` is skipped.
  dispatchWaiting(batch) {
    if (this.waiting.delete(batch)) {
      this.guard(() => this.dispatch(batch), batch.events, batch.key);
    }
  }

  // Marks the page as being left, leaves in the outbox every batch still waiting on the visit or held after a refusal,
  // and lets go of every batch this page holds. A batch goes to `ahoy.track` instead while the fallback is active, or
  // when leaving it throws. So does a batch not stored yet that the outbox cannot hold.
  stash() {
    this.leaving = true;

    clearTimeout(this.retryTimer);
    this.retryTimer = null;

    // Both pending sets are emptied before any batch is stored, because a store that trips the fallback releases
    // whatever is still pending, and a batch must not be both released and stored.
    const waiting = Array.from(this.waiting);
    const refused = this.refused.splice(0);

    this.waiting.clear();

    waiting.forEach((batch) => {
      this.guard(
        () => this.leave({ events: batch.events, tokens: batch.tokens || this.currentTokens(), key: batch.key }),
        batch.events,
        batch.key,
      );
    });
    refused.forEach((batch) => this.guard(() => this.leave(batch), batch.events, batch.key));
    this.outbox.unholdAll();
  }

  // Leaves the batch in the outbox for the next page, rewriting its stored copy under the tokens the leave settled on.
  // A batch with no outbox key, or whose stored copy cannot be rewritten, is stored (`store`). While the fallback is
  // active, its events go to `ahoy.track` through the outbox (`trackLater`).
  leave(batch) {
    if (!batch.key) {
      this.store(batch);
      return;
    }

    if (this.fallback.active()) {
      this.trackLater(batch.events, batch.tokens, batch.key);
      return;
    }

    if (this.outbox.rewrite(batch.key, batch)) {
      this.onHandOff(batch.events);
      return;
    }

    this.outbox.remove(batch.key);
    this.store({ events: batch.events, tokens: batch.tokens });
  }

  // Marks the page as in use again, removes the expired claims and the unheld batches past their maximum age or
  // unreadable, and sends the rest once the visit is ready. While batching is off or the fallback is active, or when
  // more than `maxStoredBatches` remain, they are released to `ahoy.track` instead. A fallback that becomes active
  // while the visit or the outbox's lock is awaited releases whatever has not been sent yet.
  resume() {
    this.leaving = false;

    this.guard(() => {
      this.outbox.purge();
      this.claims.purge();

      if (!this.batching || this.fallback.active()) {
        this.release();
        return;
      }

      const stored = this.outbox.unheldKeys().length;

      if (stored > this.maxStoredBatches) {
        this.fallback.trip("the outbox held " + stored + " batches when the page started");
        return;
      }

      this.ahoy.ready(() => this.guard(() => this.sendStored()));
    });
  }

  sendStored() {
    const fallbackActive = () => this.fallback.active();

    this.outbox
      .drain((batch) => this.guard(() => this.dispatch(batch), batch.events, batch.key), undefined, fallbackActive)
      .then((more) => {
        if (more && fallbackActive()) {
          this.releaseStored();
        }
      });
  }

  // Sends the batch under the tokens it was stored with, or the current ones, and removes its stored copy once every
  // event has been sent or stored again. A refused batch the outbox cannot hold keeps the stored copy as its key, so
  // the events that copy holds survive a page that dies without a leave event. Events another page has claimed, or
  // this page has sent, are not sent. While the fallback is active, the events go to `ahoy.track` through the
  // outbox (`trackLater`). When the tokens carry no visit token, they go to `ahoy.track` at once. A batch the browser
  // refuses goes to the outbox while the page is being left. Otherwise it is held for a retry, or released to
  // `ahoy.track` through the outbox (`trackLater`) once it has been refused `maxAttempts` times. A batch the transport
  // returns while its probe is unanswered was held rather than refused, so it uses up no attempt.
  dispatch(batch) {
    if (this.fallback.active()) {
      this.trackLater(batch.events, batch.tokens, batch.key);
      return;
    }

    const tokens = batch.tokens || this.currentTokens();

    // `Ahoy::BaseController` rejects a request carrying no visit token, and only `ahoy.track` opens a visit
    // part-way through a page once the visit cookie has expired.
    if (!tokens.visit_token) {
      this.track(batch.events, undefined, batch.key);
      return;
    }

    const unclaimed = this.claims.claim(batch.events);

    if (unclaimed.length === 0) {
      this.forget(batch.key);
      this.onHandOff(batch.events);
      return;
    }

    const returned = this.transport.send(unclaimed, tokens, this.leaving);
    const held = this.transport.probing;
    const returnedEvents = new Set(returned.flat());

    this.onHandOff(batch.events.filter((event) => !returnedEvents.has(event)));
    this.claims.sent(unclaimed.filter((event) => !returnedEvents.has(event)));
    this.claims.release(Array.from(returnedEvents));

    let storedCopy = batch.key;

    returned.forEach((events) => {
      const attempts = (batch.attempts || 0) + (held ? 0 : 1);

      if (this.leaving) {
        this.store({ events: events, tokens: tokens });
      } else if (attempts >= this.maxAttempts) {
        this.trackLater(events, tokens);
      } else {
        const refused = { events: events, tokens: tokens, attempts: attempts };
        const key = this.outbox.hold(refused);

        if (key) {
          refused.key = key;
        } else if (storedCopy) {
          refused.key = storedCopy;
          storedCopy = null;
        }

        this.refused.push(refused);
        this.scheduleRetry();
      }
    });

    this.forget(storedCopy);
  }

  // Trips the fallback for events the endpoint answered with an error, and sends them again through the `track`
  // option (`ahoy.track` unless one is given), with the tokens of the request the endpoint rejected. The endpoint records
  // a batch's events one at a time and rescues each failure, so an error response means it recorded none of them.
  reject(events, status, tokens) {
    this.fallback.trip("the events endpoint answered " + status);
    // The events were handed off when the browser took the request, so they are not handed off again.
    this.trackEvents(events, tokens);
  }

  // Moves everything pending here to the outbox through `trackLater`, and starts releasing what the outbox holds to
  // `ahoy.track` one batch every `releaseInterval`.
  release() {
    clearTimeout(this.retryTimer);
    this.retryTimer = null;

    const batches = Array.from(this.waiting).concat(this.refused.splice(0));

    this.waiting.clear();
    batches.forEach((batch) => this.trackLater(batch.events, batch.tokens, batch.key));
    this.releaseStored();
  }

  // Stores the events in the outbox under `tokens` when those carry a visit token, and under the current tokens
  // otherwise, in place of the copy stored under `key`. Hands them off and starts the paced release of what the outbox
  // holds (`releaseStored`). Events the outbox cannot hold trip the fallback, as in `store`, and go to `ahoy.track` at
  // once under the same tokens.
  trackLater(events, tokens, key) {
    const batchTokens = tokens && tokens.visit_token ? tokens : this.currentTokens();

    this.forget(key);

    if (this.outbox.store({ events: events, tokens: batchTokens })) {
      this.onHandOff(events);
      this.releaseStored();
    } else {
      this.tripForUnstoredBatch();
      this.track(events, batchTokens);
    }
  }

  // Hands what the outbox holds to `ahoy.track` one batch every `releaseInterval`, each with the tokens it was stored
  // with, once the visit is ready. A call
  // while a release is under way does nothing. The release stops when the page is left, leaving the rest for the next
  // page, and when the outbox cannot be read.
  releaseStored() {
    if (this.releasing) {
      return;
    }

    this.releasing = true;
    this.ahoy.ready(() => this.releaseNext());
  }

  releaseNext() {
    const stop = () => {
      this.releasing = false;
    };

    if (this.leaving) {
      stop();
      return;
    }

    try {
      this.outbox
        .drain(
          (batch) => this.trackStored(batch),
          1,
          () => this.leaving,
        )
        .then((more) => {
          // `more` reports the outbox as it stood right after the claim. The outbox is read again for a batch stored
          // since, whose `releaseStored` call returned early because this release was under way.
          if (!this.leaving && (more || this.outbox.unheldKeys().length > 0)) {
            setTimeout(() => this.releaseNext(), this.releaseInterval);
          } else {
            stop();
          }
        }, stop);
    } catch {
      stop();
    }
  }

  // Sends the stored batch's events that no other page has claimed and this page has not sent to the `track` option,
  // and removes the stored copy. It calls `trackEvents`, not `track`, because a stored batch was handed off when it was
  // stored, on this page or an earlier one.
  trackStored(batch) {
    const unclaimed = this.claims.claim(batch.events);

    this.forget(batch.key);

    if (unclaimed.length) {
      this.trackEvents(unclaimed, batch.tokens);
      this.claims.sent(unclaimed);
    }
  }

  // Stores the batch. While the fallback is active, its events go to `ahoy.track` through the outbox (`trackLater`).
  // A batch the outbox cannot hold trips the fallback and goes to `ahoy.track` at once. A full outbox means pages are
  // not emptying it, since every page does once its visit is ready, and any other failure means the browser refuses
  // the storage.
  store(batch) {
    if (this.fallback.active()) {
      this.trackLater(batch.events, batch.tokens);
      return;
    }

    if (this.outbox.store(batch)) {
      this.onHandOff(batch.events);
      return;
    }

    this.tripForUnstoredBatch();
    this.track(batch.events, batch.tokens);
  }

  tripForUnstoredBatch() {
    this.fallback.trip(this.outbox.full() ? "the outbox was full" : "the outbox could not store a batch");
  }

  scheduleRetry() {
    if (this.retryTimer) {
      return;
    }

    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.refused.splice(0).forEach((batch) => this.guard(() => this.dispatch(batch), batch.events, batch.key));
    }, this.retryDelay);
  }

  // Runs `fn`, and trips the fallback if it throws. `events` the throw may have left unsent go to `ahoy.track`, in
  // place of the copy stored under `key`.
  guard(fn, events, key) {
    try {
      fn();
    } catch (e) {
      this.fallback.trip("batching threw " + e);

      if (events) {
        this.track(events, undefined, key);
      }
    }
  }

  // Hands the events to the `track` option, leaving out those another page has claimed or this page has sent, and
  // removes the copy stored under `key`.
  track(events, tokens, key) {
    const unclaimed = this.claims.claim(events);

    this.forget(key);
    this.onHandOff(events);

    if (unclaimed.length) {
      this.trackEvents(unclaimed, tokens);
      this.claims.sent(unclaimed);
    }
  }

  // Removes the copy of a batch stored under `key`, when there is one.
  forget(key) {
    if (key) {
      this.outbox.remove(key);
    }
  }

  currentTokens() {
    return { visit_token: this.ahoy.getVisitId(), visitor_token: this.ahoy.getVisitorId() };
  }
}
